/**
 * Toast Notification Plugin
 * 
 * Usage:
 *   this.$toast.success('Operation completed successfully');
 *   this.$toast.error('Something went wrong');
 *   this.$toast.warning('Please check your input');
 *   this.$toast.info('Here is some information');
 * 
 *   // With options
 *   this.$toast.success('Message', {
 *     title: 'Custom Title',
 *     timeout: 5000,
 *     position: 'top-right'
 *   });
 * 
 *   // Using show method with full options
 *   this.$toast.show({
 *     message: 'Custom message',
 *     type: 'success',
 *     title: 'Title',
 *     timeout: 3000,
 *     position: 'bottom-right'
 *   });
 */

// Simple event bus for toast communication
class EventBus {
  constructor() {
    this.events = {};
  }

  on(event, callback) {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event].push(callback);
  }

  off(event, callback) {
    if (!this.events[event]) return;
    this.events[event] = this.events[event].filter(cb => cb !== callback);
  }

  emit(event, data) {
    if (!this.events[event]) return;
    this.events[event].forEach(callback => callback(data));
  }
}

export const toastEventBus = new EventBus();

let toastId = 0;

const defaultOptions = {
  timeout: 3000,        // Auto-close after 3 seconds (0 = never auto-close)
  position: 'top-right' // top-right, top-left, top-center, bottom-right, bottom-left, bottom-center
};

const defaultTitles = {
  success: 'Success',
  info: 'Info',
  warning: 'Warning',
  danger: 'Error'
};

const defaultIcons = {
  success: 'check_circle',
  info: 'info',
  warning: 'warning',
  danger: 'error'
};

function createToast(options) {
  const id = ++toastId;
  
  const toast = {
    id,
    message: options.message || '',
    type: options.type || 'info',
    title: options.title || defaultTitles[options.type] || defaultTitles.info,
    icon: options.icon || defaultIcons[options.type] || defaultIcons.info,
    timeout: options.timeout !== undefined ? options.timeout : defaultOptions.timeout,
    position: options.position || defaultOptions.position
  };

  toastEventBus.emit('add', toast);
  
  return id;
}

function removeToast(id) {
  toastEventBus.emit('remove', id);
}

function clearAllToasts() {
  toastEventBus.emit('clear');
}

const toast = {
  /**
   * Show a toast notification
   * @param {Object} options - Toast options
   * @param {string} options.message - The message to display
   * @param {string} options.type - Toast type: 'success', 'info', 'warning', 'danger'
   * @param {string} options.title - Optional title
   * @param {number} options.timeout - Auto-close timeout in ms (0 = no auto-close)
   * @param {string} options.position - Position: 'top-right', 'top-left', 'top-center', 'bottom-right', 'bottom-left', 'bottom-center'
   */
  show(options) {
    return createToast(options);
  },

  /**
   * Show a success toast
   * @param {string} message - The message to display
   * @param {Object} options - Additional options
   */
  success(message, options = {}) {
    return createToast({ ...options, message, type: 'success' });
  },

  /**
   * Show an info toast
   * @param {string} message - The message to display
   * @param {Object} options - Additional options
   */
  info(message, options = {}) {
    return createToast({ ...options, message, type: 'info' });
  },

  /**
   * Show a warning toast
   * @param {string} message - The message to display
   * @param {Object} options - Additional options
   */
  warning(message, options = {}) {
    return createToast({ ...options, message, type: 'warning' });
  },

  /**
   * Show a danger/error toast
   * @param {string} message - The message to display
   * @param {Object} options - Additional options
   */
  danger(message, options = {}) {
    return createToast({ ...options, message, type: 'danger' });
  },

  /**
   * Alias for danger - Show an error toast
   * @param {string} message - The message to display
   * @param {Object} options - Additional options
   */
  error(message, options = {}) {
    return createToast({ ...options, message, type: 'danger' });
  },

  /**
   * Remove a specific toast by ID
   * @param {number} id - The toast ID to remove
   */
  remove(id) {
    removeToast(id);
  },

  /**
   * Clear all toasts
   */
  clear() {
    clearAllToasts();
  }
};

// Vue plugin
export default {
  install(app) {
    // Make $toast available in all components
    app.config.globalProperties.$toast = toast;
    
    // Also provide it for Composition API
    app.provide('toast', toast);
  }
};

// Export for direct import
export { toast };
